import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import arrowIcon from '@/assets/icons/arrowIconWhite.svg';
import replyAllIcon from '@/assets/icons/replyAllIcon.svg';
import replyIcon from '@/assets/icons/replyIcon.svg';
import { Button } from '@/components/ui/button';
import { ANALYTICS_EVENTS } from '@/config/analytics/events';
import { usePostHog } from '@/config/analytics/posthog';
import {
  displayReplyAllWithContent,
  displayReplyWithContent,
  isInReadMode,
  isReadingDraft,
  openOrUpdateComposeWindow,
} from '@/config/office/office';
import { Redlining } from '@/features/redlining/components/Redlining';
import type { EmailDraft } from '@/utils/emailContentDisplayUtils';
import { useTypewriter } from '@/utils/hooks/useTypewriter';

interface RevisionDraftCardProps {
  isGenerating?: boolean;
  messageId: string;
  original: EmailDraft;
  revised: EmailDraft;
  sessionId: string;
}

export default function RevisionDraftCard({
  isGenerating = false,
  messageId,
  original,
  revised,
  sessionId,
}: RevisionDraftCardProps) {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (!hasTrackedRef.current && original.body && revised.body) {
      hasTrackedRef.current = true;
      posthog.capture(ANALYTICS_EVENTS.ASSISTANT_REDLINE_ACTIVATED, { messageId, sessionId });
    }
  }, [messageId, sessionId, original.body, revised.body, posthog]);

  const displayedRevised = useTypewriter(revised.body, { isGenerating, speed: 1 });

  const handleReply = async () => {
    try {
      await displayReplyWithContent(revised.body, true);
      posthog.capture(ANALYTICS_EVENTS.ASSISTANT_REDLINE_REPLY_CLICKED, {
        messageId,
        sessionId,
      });
      toast.success(t('assistant.revisionDraft.replyOpened'));
    } catch (error) {
      console.error('Failed to open reply:', error);
      toast.error(t('assistant.revisionDraft.replyFailed'));
    }
  };

  const handleReplyAll = async () => {
    try {
      await displayReplyAllWithContent(revised.body, true);
      posthog.capture(ANALYTICS_EVENTS.ASSISTANT_REDLINE_REPLY_ALL_CLICKED, {
        messageId,
        sessionId,
      });
      toast.success(t('assistant.revisionDraft.replyAllOpened'));
    } catch (error) {
      console.error('Failed to open reply all:', error);
      toast.error(t('assistant.revisionDraft.replyAllFailed'));
    }
  };

  const handleOpenInOutlook = async () => {
    try {
      await openOrUpdateComposeWindow({
        body: revised.body,
        subject: revised.subject,
        to: revised.to,
        ...(revised.cc && { cc: revised.cc }),
        ...(revised.bcc && { bcc: revised.bcc }),
      });
      toast.success(t('assistant.revisionDraft.applied'));
    } catch (error) {
      console.error('Failed to apply draft:', error);
      toast.error(t('assistant.revisionDraft.applyFailed'));
    }
  };

  if (!original.body && !revised.body) {
    return null;
  }

  const headers = (
    <div className="space-y-2 pt-1.5">
      {revised.to && (
        <div>
          <span className="text-secondary text-xs leading-[170%] font-semibold">
            {t('assistant.emailDraft.to')}
          </span>
          <p className="text-secondary text-xs leading-[170%] underline">{revised.to}</p>
        </div>
      )}
      {revised.cc && (
        <div>
          <span className="text-secondary text-xs leading-[170%] font-semibold">
            {t('assistant.emailDraft.cc')}
          </span>
          <p className="text-secondary text-xs leading-[170%] underline">{revised.cc}</p>
        </div>
      )}
      {revised.bcc && (
        <div>
          <span className="text-secondary text-xs leading-[170%] font-semibold">
            {t('assistant.emailDraft.bcc')}
          </span>
          <p className="text-secondary text-xs leading-[170%] underline">{revised.bcc}</p>
        </div>
      )}
      {revised.subject && (
        <div>
          <span className="text-secondary text-xs leading-[170%] font-semibold">
            {t('assistant.emailDraft.subject')}
          </span>
          <p className="text-secondary text-xs leading-[170%]">{revised.subject}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="w-full rounded-[8px] border-[0.5px] p-3">
      <div className="flex items-center justify-between">
        <p className="text-secondary text-xs leading-[170%] font-medium">Revised mail</p>
      </div>

      {headers}

      <div className="border-subtle bg-background mt-3 rounded-sm border-[0.5px] p-3">
        <Redlining
          finalText={displayedRevised}
          initialText={original.body}
          isGenerating={isGenerating}
        />
      </div>

      {!isGenerating && (
        <div className="mt-3 flex w-full justify-end gap-2 p-2">
          {isInReadMode() && !isReadingDraft() ? (
            <>
              <Button
                className="text-primary-foreground h-6 px-2 text-[10px]"
                onClick={handleReply}
              >
                <img alt="Reply" className="icon-on-primary" src={replyIcon} />
              </Button>
              <Button
                className="text-primary-foreground h-6 px-2 text-[10px]"
                onClick={handleReplyAll}
              >
                <img alt="Reply All" className="icon-on-primary" src={replyAllIcon} />
              </Button>
            </>
          ) : (
            <Button
              className="text-primary-foreground h-6 w-[85px] text-[10px]"
              onClick={handleOpenInOutlook}
            >
              Open Draft
              <img alt="Open" className="icon-on-primary" src={arrowIcon} />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
